import { useEffect, useState, useRef } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { HomePage } from '@/routes/HomePage';
import { StudyPage } from '@/routes/StudyPage';
import { WordsPage } from '@/routes/WordsPage';
import { CoursesPage } from '@/routes/CoursesPage';
import { GrammarPage } from '@/routes/GrammarPage';
import { ProfilePage } from '@/routes/ProfilePage';
import { SettingsPage } from '@/routes/SettingsPage';
import { StatsPage } from '@/routes/StatsPage';
import { AdminPage } from '@/routes/AdminPage';
import { AdminDictPage } from '@/routes/AdminDictPage';
import { NotFoundPage } from '@/routes/NotFoundPage';
import { useSettingsStore } from '@/stores/settingsStore';
import { useAuthStore } from '@/stores/authStore';
import { useSubscriptionStore } from '@/stores/subscriptionStore';
import { api, setInitData, setAiProviderCallback } from '@/shared/lib/api';
import { identifyUser, trackEvent, captureError } from '@/shared/lib/monitoring';
import { GuidedTourOverlay } from '@/features/guided-tour';
import { BackNavigationProvider } from '@/shared/lib/navigation/BackNavigationContext';
import { SwipeBackContainer } from '@/shared/components/SwipeBackContainer';
import { MainTabsKeepAlive, isMainTabPath } from '@/shared/components/MainTabsKeepAlive';

const PROVIDER_LABELS: Record<string, string> = {
  gemini: 'Gemini 2.5 Flash',
  groq: 'Llama 4 Scout via Groq',
  cerebras: 'Qwen3-235B via Cerebras',
};

function AiModelToast({ provider, endpoint, onHide }: { provider: string; endpoint: string; onHide: () => void }) {
  useEffect(() => {
    trackEvent('ai_model_toast_shown', { provider, endpoint });
    const t = setTimeout(onHide, 10000);
    return () => clearTimeout(t);
  }, [provider, endpoint]);

  return (
    <div style={{
      position: 'fixed',
      top: 12,
      left: '50%',
      transform: 'translateX(-50%)',
      background: 'var(--theme-bg-elevated)',
      border: '1px solid var(--theme-border)',
      borderRadius: 20,
      padding: '6px 14px',
      fontSize: 11,
      color: 'var(--theme-text-secondary)',
      whiteSpace: 'nowrap',
      zIndex: 9999,
      pointerEvents: 'none',
      animation: 'fadeIn 0.2s ease',
      boxShadow: 'var(--theme-shadow)',
    }}>
      🤖 {PROVIDER_LABELS[provider] ?? provider}
    </div>
  );
}

const PAGE_NAMES: Record<string, string> = {
  '/': 'home',
  '/study': 'study',
  '/words': 'words',
  '/profile': 'profile',
  '/settings': 'settings',
  '/courses': 'courses',
  '/grammar': 'grammar',
  '/stats': 'stats',
  '/admin': 'admin',
};

function PageTracker() {
  const location = useLocation();

  useEffect(() => {
    const pageName = PAGE_NAMES[location.pathname] ?? location.pathname;
    trackEvent('page_viewed', { page: pageName, path: location.pathname });
  }, [location.pathname]);

  return null;
}

function App() {
  const { theme, showAiModel, setTtsEnabled, setHapticEnabled, setAutoAdvance } = useSettingsStore();
  const { setUser, setSettings, setInitialized, setLoading, isLoading } = useAuthStore();
  const { load: loadSubscription } = useSubscriptionStore();
  const [noTelegramContext, setNoTelegramContext] = useState(false);
  const [aiProvider, setAiProvider] = useState<{ provider: string; endpoint: string } | null>(null);
  const showAiModelRef = useRef(showAiModel);
  showAiModelRef.current = showAiModel;

  useEffect(() => {
    setAiProviderCallback((provider, endpoint) => {
      if (showAiModelRef.current) {
        setAiProvider({ provider, endpoint });
      }
    });
  }, []);

  useEffect(() => {
    // Применяем тему
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => {
    // Инициализация Telegram WebApp и аутентификация
    async function initApp() {
      const webApp = (window as any).Telegram?.WebApp;
      const isDev = import.meta.env.DEV;

      let authToken: string;
      if (webApp?.initData) {
        webApp.ready();
        webApp.expand();
        authToken = webApp.initData;
      } else if (isDev) {
        // Development: используем тестового пользователя
        authToken = 'test_user_666';
      } else {
        // Production без Telegram-контекста — приложение открыто напрямую в браузере
        setNoTelegramContext(true);
        setLoading(false);
        setInitialized(true);
        return;
      }

      setInitData(authToken);

      try {
        const result = await api.post<{ user: any; settings: any }>('/auth/init', {});
        setUser(result.user);
        setSettings(result.settings);
        if (result.settings?.tts_enabled != null) setTtsEnabled(result.settings.tts_enabled);
        if (result.settings?.haptic_enabled != null) setHapticEnabled(result.settings.haptic_enabled);
        if (result.settings?.auto_advance != null) setAutoAdvance(result.settings.auto_advance);
        // Идентифицируем пользователя в мониторинге
        if (result.user?.id) identifyUser(result.user.id);
        // Загружаем статус подписки после авторизации
        loadSubscription();
        // Трекаем открытие приложения
        trackEvent('app_opened', {
          is_premium: result.user?.is_premium ?? false,
          language_code: result.user?.language_code ?? 'unknown',
          platform: webApp ? 'telegram' : 'web',
        });
      } catch (err) {
        console.error('Auth init failed:', err);
        captureError(err, { context: 'auth_init' });
      } finally {
        setLoading(false);
        setInitialized(true);
      }
    }

    initApp();
  }, []);

  if (noTelegramContext) {
    // Пропускаем /admin/* — админка работает без Telegram
    if (window.location.pathname.startsWith('/admin')) {
      return (
        <Routes>
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/admin/dict" element={<AdminDictPage />} />
          <Route path="*" element={<div style={{ color: '#fff', padding: 40 }}>Admin: страница не найдена</div>} />
        </Routes>
      );
    }
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '40px 24px',
        textAlign: 'center',
        background: 'var(--theme-bg)',
        color: 'var(--theme-text)',
        gap: '16px',
      }}>
        <div style={{ fontSize: '64px' }}>✈️</div>
        <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600 }}>
          Откройте через Telegram
        </h2>
        <p style={{ margin: 0, color: 'var(--theme-text-secondary)', fontSize: '15px', maxWidth: '280px', lineHeight: 1.5 }}>
          Это приложение работает только внутри Telegram. Запустите бота, чтобы открыть его.
        </p>
      </div>
    );
  }

  // Блокируем рендер до завершения auth — предотвращает гонку,
  // при которой компоненты успевают сделать API-запросы до вызова setInitData
  if (isLoading) {
    return null;
  }

  const location = useLocation();
  const isMainTab = isMainTabPath(location.pathname);

  return (
    <BackNavigationProvider>
      <PageTracker />
      {aiProvider && (
        <AiModelToast provider={aiProvider.provider} endpoint={aiProvider.endpoint} onHide={() => setAiProvider(null)} />
      )}
      <GuidedTourOverlay />
      <SwipeBackContainer>
        <div style={{ display: isMainTab ? 'flex' : 'none', flexDirection: 'column', flex: 1, width: '100%' }}>
          <MainTabsKeepAlive activePath={location.pathname} />
        </div>
        {!isMainTab && (
          <Routes>
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/courses" element={<CoursesPage />} />
            <Route path="/stats" element={<StatsPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/admin/dict" element={<AdminDictPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        )}
      </SwipeBackContainer>
    </BackNavigationProvider>
  );
}

export default App;
